import { getPdtFreshnessStatus, PdtFreshnessStatus } from './pdt';

/**
 * PDT malware scan status (aligned with labor category and document scan flows).
 *
 * State flow: PENDING → SCANNING → CLEAN | INFECTED | ERROR
 */
export const PDT_SCAN_STATUS = {
  CLEAN: 'CLEAN',
  ERROR: 'ERROR',
  INFECTED: 'INFECTED',
  PENDING: 'PENDING',
  SCANNING: 'SCANNING',
} as const;

export type PdtScanStatus = (typeof PDT_SCAN_STATUS)[keyof typeof PDT_SCAN_STATUS];

export const PDT_SCAN_NOTICE_MESSAGE = {
  [PDT_SCAN_STATUS.INFECTED]:
    'The PDT is infected with a virus or malware and cannot be used. Please download and sign a new copy of the file.',
  [PDT_SCAN_STATUS.SCANNING]: 'The PDT has been uploaded and is awaiting malware/virus scan',
} as const;

export const PDT_SCAN_EDIT_ARIA_LABEL = {
  [PDT_SCAN_STATUS.INFECTED]: 'PDT infected',
  [PDT_SCAN_STATUS.SCANNING]: 'PDT awaiting malware scan',
} as const;

export type PdtScanNoticeVariant = 'error' | 'warning';

export interface PdtScanNotice {
  message: string;
  variant: PdtScanNoticeVariant;
}

/**
 * User-facing notice for in-progress or failed malware scans.
 * Returns null for statuses that do not require a list-row notice.
 */
export function getPdtScanNotice(pdtScanStatus: PdtScanStatus | string | null | undefined): PdtScanNotice | null {
  if (pdtScanStatus === PDT_SCAN_STATUS.SCANNING) {
    return {
      message: PDT_SCAN_NOTICE_MESSAGE.SCANNING,
      variant: 'warning',
    };
  }

  if (pdtScanStatus === PDT_SCAN_STATUS.INFECTED) {
    return {
      message: PDT_SCAN_NOTICE_MESSAGE.INFECTED,
      variant: 'error',
    };
  }

  return null;
}

export type PdtListAlertVariant = 'error' | 'warning';

/**
 * Highest-priority PDT alert for labor category list UI (scan status overrides freshness).
 */
export function getPdtListAlertVariant(
  pdtScanStatus: PdtScanStatus | string | null | undefined,
  pdtUploadedAt: Date | string | null | undefined,
  referenceDate: Date = new Date(),
): PdtListAlertVariant | null {
  const scanNotice = getPdtScanNotice(pdtScanStatus);
  if (scanNotice) {
    return scanNotice.variant;
  }

  const freshness = getPdtFreshnessStatus(pdtUploadedAt, referenceDate);
  if (freshness === PdtFreshnessStatus.EXPIRED) {
    return 'error';
  }

  if (freshness === PdtFreshnessStatus.NEEDS_UPDATE) {
    return 'warning';
  }

  return null;
}

/**
 * Accessible suffix for the Edit labor category link (scan status overrides freshness).
 */
export function getPdtEditLinkAriaLabelSuffix(
  pdtScanStatus: PdtScanStatus | string | null | undefined,
  pdtUploadedAt: Date | string | null | undefined,
  referenceDate: Date = new Date(),
): string | null {
  if (pdtScanStatus === PDT_SCAN_STATUS.INFECTED) {
    return PDT_SCAN_EDIT_ARIA_LABEL.INFECTED;
  }

  if (pdtScanStatus === PDT_SCAN_STATUS.SCANNING) {
    return PDT_SCAN_EDIT_ARIA_LABEL.SCANNING;
  }

  const freshness = getPdtFreshnessStatus(pdtUploadedAt, referenceDate);
  if (freshness === PdtFreshnessStatus.EXPIRED) {
    return 'PDT expired';
  }

  if (freshness === PdtFreshnessStatus.NEEDS_UPDATE) {
    return 'PDT will expire soon';
  }

  return null;
}
